import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import {
  SUPPORT_SERVICES,
  type SupportServiceId,
} from "@modules/support/constants/supportServices";
import { ChevronRight } from "lucide-react";

type SupportServiceListProps = {
  selectedServiceId: SupportServiceId;
  onSelect: (serviceId: SupportServiceId) => void;
};

export function SupportServiceList({
  selectedServiceId,
  onSelect,
}: SupportServiceListProps) {
  const { t } = useTranslation();

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">
          {t("hub.support.services_heading", {
            defaultValue: "Services",
          })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ItemGroup className="gap-3">
          {SUPPORT_SERVICES.map((service) => {
            const isSelected = service.id === selectedServiceId;
            const Icon = service.icon;

            return (
              <Item
                key={service.id}
                variant="outline"
                size="xs"
                role="button"
                tabIndex={0}
                className={cn(
                  "flex-nowrap cursor-pointer",
                  isSelected && "border-primary",
                )}
                onClick={() => onSelect(service.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(service.id);
                  }
                }}>
                <ItemMedia
                  className={cn(
                    "size-10 rounded-full group-hover/item:bg-transparent !self-center !translate-y-0",
                    service.iconBgClass,
                  )}>
                  <Icon
                    className={cn("size-5", service.iconColorClass)}
                    aria-hidden
                  />
                </ItemMedia>
                <ItemContent className="min-w-0">
                  <ItemTitle className="font-semibold">
                    {t(service.titleKey, {
                      defaultValue: service.titleDefault,
                    })}
                  </ItemTitle>
                  <ItemDescription className="line-clamp-none">
                    {t(service.descriptionKey, {
                      defaultValue: service.descriptionDefault,
                    })}
                  </ItemDescription>
                </ItemContent>
                <ItemActions>
                  <ChevronRight
                    className={cn(
                      "size-4 text-muted-foreground",
                      isSelected && "text-primary",
                      !isSelected && "group-hover/item:text-foreground",
                    )}
                    aria-hidden
                  />
                </ItemActions>
              </Item>
            );
          })}
        </ItemGroup>
      </CardContent>
    </Card>
  );
}
